import React, { Suspense, useEffect, useState } from 'react';
import { Main } from '../styled';
import Spinner from '../../components/utilities/Spinner';
import { useNavigate } from 'react-router-dom';
import { mockStore } from '../../utility/mockStore';
import {
    Card, Row, Col, Typography, Space, Tag, Button, Table, Divider, Avatar, Statistic, Timeline
} from 'antd';
import {
    TrophyOutlined, DollarCircleOutlined, MobileOutlined,
    EyeOutlined, BellOutlined,
    HistoryOutlined, RocketOutlined,
    DashboardOutlined, ClockCircleOutlined, CheckCircleOutlined,
    CloseCircleOutlined, FireOutlined, StarOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
    PieChart, Pie, Cell
} from 'recharts';

const { Title, Text } = Typography;

const trendData = [
    { day: 'Mon', value: 85000, count: 3 },
    { day: 'Tue', value: 120000, count: 4 },
    { day: 'Wed', value: 65000, count: 2 },
    { day: 'Thu', value: 180000, count: 5 },
    { day: 'Fri', value: 145000, count: 4 },
    { day: 'Sat', value: 95000, count: 3 },
    { day: 'Sun', value: 110000, count: 3 },
];

const conditionData = [
    { name: 'New', value: 12, color: '#52c41a' },
    { name: 'Good', value: 45, color: '#1890ff' },
    { name: 'Used - Worn', value: 38, color: '#faad14' },
    { name: 'Damaged', value: 29, color: '#ff4d4f' },
];

const DashboardPage = () => {
    const navigate = useNavigate();
    const [applications, setApplications] = useState([]);
    const [activities, setActivities] = useState([]);

    useEffect(() => {
        setApplications(mockStore.getApplications());
        setActivities(mockStore.getRecentActivities());
    }, []);

    const getStatusColor = (status) => {
        if (status === 'Accepted') return 'success';
        if (status === 'Rejected') return 'error';
        if (status === 'Quoted') return 'warning';
        return 'processing';
    };

    const handleViewDetails = (appId) => {
        navigate(`/History/${appId}`);
    };

    const getActivityAppId = (text = '') => {
        const match = text.match(/APP-\d+/);
        return match ? match[0] : null;
    };

    const latestActivity = activities[0];
    const latestActivityAppId = getActivityAppId(latestActivity?.text);

    const handleViewActivity = (activity) => {
        const appId = getActivityAppId(activity?.text);
        if (appId) {
            handleViewDetails(appId);
            return;
        }
        navigate('/history');
    };

    // Calculate dynamic stats
    const pendingRequestsCount = applications.filter(a => a.status === 'Pending').length;
    const approvedRequestsCount = applications.filter(a => a.status === 'Accepted').length;
    const rejectedRequestsCount = applications.filter(a => a.status === 'Rejected' || a.status === 'Declined').length;
    const submittedQuotesCount = applications.filter(a => a.status === 'Accepted' || a.status === 'Rejected' || a.status === 'Quoted').length;

    // Total quote value of approved quotes
    const approvedValue = applications
        .filter(a => a.status === 'Accepted')
        .reduce((sum, item) => sum + (item.quotedAmount || 0), 0);

    const acceptanceRate = submittedQuotesCount > 0
        ? ((approvedRequestsCount / submittedQuotesCount) * 100).toFixed(1)
        : '0.0';

    const columns = [
        {
            title: 'App ID',
            dataIndex: 'id',
            key: 'id',
            render: (text) => <Text strong style={{ color: '#1890ff' }}>{text}</Text>
        },
        {
            title: 'Device Model',
            dataIndex: 'deviceModel',
            key: 'deviceModel',
            render: (text, record) => (
                <Space size="middle">
                    <Avatar src={record.images[0]} icon={<MobileOutlined />} shape="square" style={{ borderRadius: '6px' }} />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Quoted Price',
            dataIndex: 'quotedAmount',
            key: 'quotedAmount',
            render: (amount, record) => (
                <Text strong>
                    {amount ? `₹${amount.toLocaleString()}` : `Initial: ₹${record.initialQuote?.toLocaleString()}`}
                </Text>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)} style={{ minWidth: '85px', textAlign: 'center' }}>
                    {status}
                </Tag>
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button
                    type="primary"
                    ghost
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetails(record.id)}
                    size="small"
                >
                    View details
                </Button>
            )
        }
    ];

    // Map Timeline icons
    const getActivityTimelineColor = (type) => {
        if (type === 'success') return 'green';
        if (type === 'error') return 'red';
        if (type === 'warning') return 'orange';
        return 'blue';
    };

    return (
        <Main>
            <Suspense fallback={<Spinner />}>
                <div>
                    <Title level={3}><DashboardOutlined style={{ marginRight: '4px', color: '#1890ff' }} /> Vendor Dashboard</Title>

                    {/* Latest Activity Notification */}
                    {latestActivity && (
                        <Card
                            variant="borderless"
                            style={{
                                background: 'linear-gradient(135deg, #e6f7ff 0%, #f6ffed 100%)',
                                border: '1px solid #91d5ff',
                                borderRadius: '12px',
                                marginBottom: '24px',
                                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.08)'
                            }}
                            styles={{ body: { padding: '20px 24px' } }}
                        >
                            <Row align="middle" justify="space-between" gutter={[16, 16]}>
                                <Col xs={24} md={18}>
                                    <Space size="middle" align="start">
                                        <div style={{
                                            background: '#1890ff',
                                            borderRadius: '50%',
                                            padding: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 4px 10px rgba(24, 144, 255, 0.3)',
                                            animation: 'alertGlow 2s infinite ease-in-out',
                                            flexShrink: 0
                                        }}>
                                            <BellOutlined style={{ color: '#fff', fontSize: '20px' }} />
                                        </div>
                                        <div>
                                            <Title level={4} style={{ margin: 0, color: '#0958d9', fontWeight: 700 }}>
                                                Latest Activity
                                            </Title>
                                            <Text style={{ color: '#1f1f1f', fontSize: '14px', fontWeight: 500 }}>
                                                {latestActivity.text}
                                            </Text>
                                            <div>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>{latestActivity.time}</Text>
                                            </div>
                                        </div>
                                    </Space>
                                </Col>
                                <Col xs={24} md={6} style={{ textAlign: 'right' }}>
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<EyeOutlined />}
                                        onClick={() => latestActivityAppId ? handleViewDetails(latestActivityAppId) : navigate('/history')}
                                        style={{
                                            height: '44px',
                                            borderRadius: '8px',
                                            fontWeight: 'bold',
                                            boxShadow: '0 4px 10px rgba(24, 144, 255, 0.25)'
                                        }}
                                        className="pulse-btn"
                                    >
                                        View
                                    </Button>
                                </Col>
                            </Row>
                        </Card>
                    )}

                    {/* KPI Statistics Row */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
                        <Col xs={24} sm={12} lg={6}>
                            <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)', borderRadius: '12px', borderLeft: '4px solid #faad14' }}>
                                <Statistic
                                    title="Pending Requests"
                                    value={pendingRequestsCount}
                                    prefix={<ClockCircleOutlined style={{ color: '#faad14', marginRight: '6px' }} />}
                                    valueStyle={{ color: '#faad14', fontWeight: 'bold' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)', borderRadius: '12px', borderLeft: '4px solid #52c41a' }}>
                                <Statistic
                                    title="Approved Requests"
                                    value={approvedRequestsCount}
                                    prefix={<CheckCircleOutlined style={{ color: '#52c41a', marginRight: '6px' }} />}
                                    valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)', borderRadius: '12px', borderLeft: '4px solid #ff4d4f' }}>
                                <Statistic
                                    title="Rejected Requests"
                                    value={rejectedRequestsCount}
                                    prefix={<CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: '6px' }} />}
                                    valueStyle={{ color: '#ff4d4f', fontWeight: 'bold' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)', borderRadius: '12px', borderLeft: '4px solid #1890ff' }}>
                                <Statistic
                                    title="Total Payout Approved"
                                    value={approvedValue}
                                    prefix={<DollarCircleOutlined style={{ color: '#1890ff', marginRight: '6px' }} />}
                                    suffix="INR"
                                    valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Charts Row */}
                    <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
                        <Col xs={24} lg={15}>
                            <Card
                                title={<Text strong style={{ fontSize: '16px' }}>Quote Submission Trends (Past Week)</Text>}
                                variant="borderless"
                                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)', borderRadius: '12px', height: '100%' }}
                                styles={{ body: { padding: '24px' } }}
                            >
                                <div style={{ width: '100%', height: '300px' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart
                                            data={trendData}
                                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                        >
                                            <defs>
                                                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#1890ff" stopOpacity={0.2} />
                                                    <stop offset="95%" stopColor="#1890ff" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                            <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fill: '#8c8c8c' }} />
                                            <YAxis tickLine={false} axisLine={false} tick={{ fill: '#8c8c8c' }} />
                                            <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Quoted Value']} />
                                            <Area type="monotone" dataKey="value" stroke="#1890ff" strokeWidth={2} fillOpacity={1} fill="url(#colorValue)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>
                        </Col>

                        <Col xs={24} lg={9}>
                            <Card
                                title={<Text strong style={{ fontSize: '16px' }}>Condition Distribution</Text>}
                                variant="borderless"
                                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)', borderRadius: '12px', height: '100%' }}
                                styles={{ body: { padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } }}
                            >
                                <div style={{ width: '100%', height: '220px' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={conditionData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {conditionData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => [`${value} Devices`, 'Count']} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '16px', marginTop: '16px' }}>
                                    {conditionData.map((item, idx) => (
                                        <Space key={idx} size="small">
                                            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: item.color }} />
                                            <Text style={{ fontSize: '12px', color: '#595959' }}>{item.name} ({item.value})</Text>
                                        </Space>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Performance & Activities Row */}
                    <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
                        <Col xs={24} md={12} lg={12}>
                            <Card
                                title={
                                    <Space>
                                        <TrophyOutlined style={{ color: '#fa8c16' }} />
                                        <Text strong style={{ fontSize: '16px' }}>Vendor Performance Summary</Text>
                                    </Space>
                                }
                                variant="borderless"
                                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)', borderRadius: '12px', height: '100%' }}
                                styles={{ body: { padding: '24px' } }}
                            >
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <Card style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: '8px' }} styles={{ body: { padding: '16px' } }}>
                                            <Statistic
                                                title="Win/Acceptance Rate"
                                                value={parseFloat(acceptanceRate)}
                                                suffix="%"
                                                prefix={<FireOutlined style={{ color: '#fa8c16' }} />}
                                                valueStyle={{ color: '#fa8c16' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col span={12}>
                                        <Card style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: '8px' }} styles={{ body: { padding: '16px' } }}>
                                            <Statistic
                                                title="Avg. Quote Response"
                                                value={12}
                                                suffix="mins"
                                                prefix={<ClockCircleOutlined style={{ color: '#52c41a' }} />}
                                                valueStyle={{ color: '#52c41a' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col span={12}>
                                        <Card style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: '8px' }} styles={{ body: { padding: '16px' } }}>
                                            <Statistic
                                                title="Quote Participation"
                                                value={95}
                                                suffix="%"
                                                prefix={<ThunderboltOutlined style={{ color: '#1890ff' }} />}
                                                valueStyle={{ color: '#1890ff' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col span={12}>
                                        <Card style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: '8px' }} styles={{ body: { padding: '16px' } }}>
                                            <Statistic
                                                title="Overall Trust Rating"
                                                value={4.8}
                                                suffix="/ 5.0"
                                                prefix={<StarOutlined style={{ color: '#fadb14' }} />}
                                                valueStyle={{ color: '#fadb14' }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        <Col xs={24} md={12} lg={12}>
                            <Card
                                title={
                                    <Space>
                                        <HistoryOutlined style={{ color: '#1890ff' }} />
                                        <Text strong style={{ fontSize: '16px' }}>Recent Activities Log</Text>
                                    </Space>
                                }
                                variant="borderless"
                                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)', borderRadius: '12px', height: '100%' }}
                                styles={{ body: { padding: '24px' } }}
                            >
                                <Timeline
                                    style={{ marginTop: '8px' }}
                                    items={activities.map((act) => ({
                                        key: act.id,
                                        color: getActivityTimelineColor(act.type),
                                        children: (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div>
                                                    <Text style={{ fontSize: '14px', color: '#262626' }}>{act.text}</Text>
                                                    <div>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>{act.time}</Text>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => handleViewActivity(act)}
                                                    style={{ flexShrink: 0, marginLeft: '12px', paddingRight: 0 }}
                                                >
                                                    View
                                                </Button>
                                            </div>
                                        ),
                                    }))}
                                />
                            </Card>
                        </Col>
                    </Row>
                </div>
            </Suspense>
        </Main>
    );
};

export default DashboardPage;
